'use client';

import { Button } from '@coolblue-development/freddy';
import pickUpInStoreTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/pickUpInStoreTrigger';
import orderBlockSummaryPickUpInStoreTrigger from '@coolblue-development/ga4-events/triggers/OrderBlockSummary/pickUpInStoreTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { StoresStockOverlay } from '@coolblue-development/next-stores/client';
import { getNumberOfStoresAvailable } from '@features/product/components/StorePickup/utils/getNumberOfStoresAvailable';
import type { Product } from '@features/product/typings';
import type { Store } from '@features/product-page/typings';
import { MainProduct } from '@features/second-chance/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

type StorePickupButtonProps = {
  product: Product | MainProduct;
  dateOverride?: string;
  isOrderSummary?: boolean;
  showAlternativePickUpButtonText?: boolean;
  preferredStore?: Store;
};

type StoresStockOverlayProduct = Parameters<typeof StoresStockOverlay>[0]['product'];

export function StorePickupButton({
  product,
  dateOverride,
  isOrderSummary,
  showAlternativePickUpButtonText,
  preferredStore
}: StorePickupButtonProps) {
  const { trackEvent } = useAnalytics();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('webshop.productPage');

  const handleLinkClick = () => {
    setOpen(true);
    trackEvent(
      isOrderSummary
        ? orderBlockSummaryPickUpInStoreTrigger({
            sub_feature: 'pick_up_button'
          })
        : pickUpInStoreTrigger()
    );
  };

  const numberOfStoresAvailable = getNumberOfStoresAvailable(product as Product);
  const buttonAlternativeText =
    numberOfStoresAvailable === 1
      ? t('storePickup.buttonAlternativeSingular')
      : t('storePickup.buttonAlternative', {
          storesNumber: numberOfStoresAvailable
        });

  return (
    <>
      <Button type='button' onClick={handleLinkClick} variant='secondary' fullWidth>
        {showAlternativePickUpButtonText && numberOfStoresAvailable > 0
          ? buttonAlternativeText
          : t('storePickup.button')}
      </Button>

      <StoresStockOverlay
        open={open}
        onClose={() => setOpen(false)}
        product={product as unknown as StoresStockOverlayProduct}
        personalPrice={'personalPrice' in product ? product.personalPrice : undefined}
        timeTravelOverride={dateOverride}
        {...(!!preferredStore?.id && { preferredStockLocationId: Number(preferredStore.id) })}
      />
    </>
  );
}
